// Risk Assessments: ONE scoring matrix for every RA - the page, the PDF, the AI generator, Upload & convert and the Hey Captain
// library (REPORT-ra-organise.md). It is the SMS-25 matrix the AI generator has always drawn: Severity 1-5 (rows) x Likelihood
// A-E (columns), a hazard scored as a code "5C" (severity 5, likelihood C) -> Low / Medium / High. Work with a residual rating
// of High needs the Captain's approval. Plain JS: tests/ra-organise.test.mjs, tests/ra-library.test.mjs.
// Hazards scored the old library way (likelihood 1-5 x severity 1-5) are read as codes: the severity is kept and likelihood
// 1-5 becomes A-E (toCode), so they show on the same matrix; a save writes the code.
export const SEVERITY = { 1: 'Insignificant injury', 2: 'Minor injury', 3: 'Serious injury', 4: 'Near fatality', 5: 'Fatality' }
export const LIKELIHOOD = { A: 'Very low', B: 'Low', C: 'Medium', D: 'High', E: 'Very high' }
export const LETTERS = 'ABCDE'
// severity row -> the band of each likelihood column A-E
export const MATRIX = {
  1: { A: 'low', B: 'low', C: 'low', D: 'low', E: 'low' },
  2: { A: 'low', B: 'low', C: 'low', D: 'low', E: 'medium' },
  3: { A: 'low', B: 'low', C: 'medium', D: 'medium', E: 'medium' },
  4: { A: 'low', B: 'medium', C: 'high', D: 'high', E: 'high' },
  5: { A: 'medium', B: 'medium', C: 'high', D: 'high', E: 'high' },
}
// Low green, Medium orange, High red: bg / tx = the soft chip, solid / stx = the matrix cells of the PDF
export const BANDS = {
  low: { label: 'Low', bg: '#dcf2e5', tx: '#145c37', solid: '#82ad8b', stx: '#1f3a27' },
  medium: { label: 'Medium', bg: '#fde3cc', tx: '#8a3e00', solid: '#e39a5b', stx: '#3d1d02' },
  high: { label: 'High', bg: '#f9dadc', tx: '#94202b', solid: '#cd6f76', stx: '#3b1014' },
}
export const BAND_RANK = { low: 1, medium: 2, high: 3 }
export const BAND_TEXT = {
  low: 'Acceptable. Proceed; maintain the existing controls.',
  medium: 'Tolerable (ALARP). Proceed only under permit with the additional controls in place and monitored.',
  high: 'Not acceptable. Do not proceed until further controls reduce the risk; a residual High needs the Captain\'s approval.',
}
export const SCORES = []
for (const s of [1, 2, 3, 4, 5]) for (const l of 'ABCDE') SCORES.push(s + l)

export const inScale = n => n !== '' && n !== null && n !== undefined && Number.isInteger(Number(n)) && Number(n) >= 1 && Number(n) <= 5
// "5C" -> { s: 5, l: 'C' } (null when it is not a code)
export function parseCode(code) {
  const m = /^([1-5])\s*([A-E])$/i.exec(String(code == null ? '' : code).trim())
  return m ? { s: +m[1], l: m[2].toUpperCase() } : null
}
export const bandOfCode = code => { const p = parseCode(code); return p ? MATRIX[p.s][p.l] : '' }
// the old library scoring (likelihood 1-5, severity 1-5) -> the code: severity kept, likelihood 1-5 = A-E
export const toCode = (likelihood, severity) => (inScale(likelihood) && inScale(severity) ? String(Number(severity)) + LETTERS[Number(likelihood) - 1] : '')
export const isNumericHazard = h => !!h && inScale(h.likelihood) && inScale(h.severity)

// the code of a hazard: which = 'initial' | 'residual' ('' when not rated)
export function hazardCode(h, which = 'initial') {
  if (!h) return ''
  const res = which === 'residual'
  const c = parseCode(res ? h.residual_score : h.initial_score)
  if (c) return c.s + c.l
  return res ? toCode(h.residual_likelihood, h.residual_severity) : toCode(h.likelihood, h.severity)
}
// -> { code, band, label, text } or null when the hazard has no rating there
export function hazardRisk(h, which = 'initial') {
  const code = hazardCode(h, which)
  const band = bandOfCode(code)
  return band ? { code, band, label: BANDS[band].label, text: code, score: null, numeric: false } : null
}
// the worst residual rating of a list of hazards ('' when none is rated)
export function worstResidual(hazards) {
  let best = ''
  for (const h of hazards || []) { const r = hazardRisk(h, 'residual'); if (r && BAND_RANK[r.band] > (BAND_RANK[best] || 0)) best = r.band }
  return best
}
// a residual High needs the Captain's approval
export const needsCaptainApproval = hazards => worstResidual(hazards) === 'high'

// one hazard in the standard form: the scores as codes (old likelihood / severity numbers converted and dropped), the band labels
export function toStandardHazard(h) {
  if (!h || typeof h !== 'object') return h
  const { likelihood, severity, residual_likelihood, residual_severity, ...rest } = h
  const i = hazardCode(h, 'initial'), r = hazardCode(h, 'residual')
  return { ...rest, initial_score: i, residual_score: r, initial_risk: i ? BANDS[bandOfCode(i)].label : '', residual_risk: r ? BANDS[bandOfCode(r)].label : '' }
}

// ---- AI generator / Upload & convert: the same scoring ----
export const SCORING_PROMPT = 'Scoring uses the SMS-25 matrix: Severity 1-5 x Likelihood A-E, written as a code: severity number then likelihood letter (e.g. "5C").\n' +
  'Severity: 1=insignificant injury, 2=minor injury, 3=serious injury, 4=near fatality, 5=fatality.\n' +
  'Likelihood: A=very low, B=low, C=medium, D=high, E=very high.\n' +
  'Rating: severity 1: all Low; 2: A-D Low, E Medium; 3: A-B Low, C-E Medium; 4: A Low, B Medium, C-E High; 5: A-B Medium, C-E High. ' +
  'Work with a residual rating of High needs the Captain\'s approval.'
// one hazard as the AI returned it -> { initial_score, residual_score, initial_risk, residual_risk }. A code ("5C") is used; numbers
// (likelihood / severity 1-5) are converted; a missing residual = the initial; a residual worse than the initial is brought down:
// its severity never above the initial's and its likelihood never above the initial's (controls reduce likelihood, not severity).
// Nothing usable -> blank (the page shows the hazard unrated).
export function normaliseAiHazard(h) {
  const pick = (code, l, s) => { const c = parseCode(code); if (c) return c; const n = toCode(l, s); return n ? parseCode(n) : null }
  const i = pick(h.initial_score, h.likelihood, h.severity)
  if (!i) return { initial_score: '', residual_score: '', initial_risk: '', residual_risk: '' }
  let r = pick(h.residual_score, h.residual_likelihood, h.residual_severity) || { ...i }
  r = { s: Math.min(r.s, i.s), l: LETTERS.indexOf(r.l) > LETTERS.indexOf(i.l) ? i.l : r.l }
  const ic = i.s + i.l, rc = r.s + r.l
  return { initial_score: ic, residual_score: rc, initial_risk: BANDS[bandOfCode(ic)].label, residual_risk: BANDS[bandOfCode(rc)].label }
}
